import { useQuery, useMutation, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  listItemImages,
  uploadItemImage,
  updateItemImage,
  reorderItemImages,
  deleteItemImage,
  listItems,
  getItem,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListItemImagesRequestSchema, UploadItemImageRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { ItemImage } from '@/gen/cortado/v1/invoices_pb'
import { isValidUUID } from '@/lib/uuid'
import { readFileAsBase64 } from '@/lib/file-utils'

/** Limits enforced by the backend (pkg/services/catalogimage). Mirrored for client-side feedback. */
export const ITEM_IMAGE_LIMITS = {
  MAX_IMAGES: 10,
  MAX_BYTES: 10 * 1024 * 1024,
  MAX_ALT_TEXT: 256,
  ACCEPT: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
  } as const,
} as const

export function useItemImages(itemId: string | undefined) {
  const enabled = isValidUUID(itemId)
  const request = useMemo(() => create(ListItemImagesRequestSchema, { itemId: itemId ?? '' }), [itemId])
  const query = useQuery(listItemImages, request, { enabled })
  return {
    images: query.data?.images ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Invalidate the gallery query and every item query that embeds `images`. */
function useInvalidateItemImageCaches() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  return () => {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listItemImages, transport, cardinality: undefined }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listItems, transport, cardinality: undefined }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: getItem, transport, cardinality: undefined }),
    })
  }
}

export function useUploadItemImage() {
  const invalidate = useInvalidateItemImageCaches()
  const mutation = useMutation(uploadItemImage, { onSuccess: invalidate })

  const upload = async (itemId: string, file: File, altText?: string): Promise<ItemImage> => {
    const fileBase64 = await readFileAsBase64(file)
    const response = await mutation.mutateAsync(
      create(UploadItemImageRequestSchema, {
        itemId,
        filename: file.name,
        fileBase64,
        altText,
      }),
    )
    if (!response.image) throw new Error('Upload failed: no image returned')
    return response.image
  }

  return { upload, mutation, isUploading: mutation.isPending }
}

export function useUpdateItemImage() {
  const invalidate = useInvalidateItemImageCaches()
  const mutation = useMutation(updateItemImage, { onSuccess: invalidate })
  return { mutation }
}

export function useReorderItemImages() {
  const invalidate = useInvalidateItemImageCaches()
  const mutation = useMutation(reorderItemImages, { onSuccess: invalidate })
  return { mutation }
}

export function useDeleteItemImage() {
  const invalidate = useInvalidateItemImageCaches()
  const mutation = useMutation(deleteItemImage, { onSuccess: invalidate })
  return { mutation }
}

/** The cover image of an item, falling back to the first by sort order. */
export function primaryItemImage(images: ItemImage[] | undefined): ItemImage | undefined {
  if (!images || images.length === 0) return undefined
  return images.find((img) => img.isPrimary) ?? [...images].sort((a, b) => a.sortOrder - b.sortOrder)[0]
}
